import type { Party, PartyInput } from '@/api/generated/model';
import { PartyInputType } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import { CountrySelect } from '@/components/ui/country-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/i18n/language-hooks';
import { useEffect, useState } from 'react';

const PARTY_TYPES = Object.values(PartyInputType);

export function partyToInput(party: Party): PartyInput {
  return {
    name: party.name,
    type: party.type,
    countryOfResidence: party.countryOfResidence,
    taxId: party.taxId,
    netWorth: party.netWorth,
    netWorthCurrency: party.netWorthCurrency,
  };
}

interface PartyEditDialogProps {
  party: Party;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (input: PartyInput) => Promise<void>;
  onDelete: () => Promise<void>;
}

export function PartyEditDialog({
  party,
  open,
  onOpenChange,
  onSave,
  onDelete,
}: PartyEditDialogProps) {
  const { t } = useLanguage();
  const [input, setInput] = useState<PartyInput>(() => partyToInput(party));

  useEffect(() => {
    setInput(partyToInput(party));
  }, [party]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{party.name}</DialogTitle>
        </DialogHeader>
        <div className='space-y-3'>
          <Input
            value={input.name}
            onChange={(event) => setInput({ ...input, name: event.target.value })}
            placeholder={t('settings.parties.name')}
          />
          <Select
            value={input.type}
            onValueChange={(type) => setInput({ ...input, type: type as PartyInput['type'] })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PARTY_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CountrySelect
            value={input.countryOfResidence ?? ''}
            onChange={(countryOfResidence) => setInput({ ...input, countryOfResidence })}
            placeholder={t('settings.parties.country')}
          />
          <Input
            type='number'
            value={input.netWorth ?? ''}
            onChange={(event) => setInput({ ...input, netWorth: Number(event.target.value) })}
            placeholder={t('settings.parties.netWorth')}
          />
          <div className='flex justify-end gap-2 pt-2'>
            <Button type='button' variant='outline' onClick={() => void onDelete()}>
              {t('common.delete')}
            </Button>
            <Button type='button' onClick={() => void onSave(input)}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface PartyFormPanelProps {
  initial?: PartyInput;
  onSubmit: (input: PartyInput) => Promise<void>;
  onCancel: () => void;
}

export function PartyFormPanel({ initial, onSubmit, onCancel }: PartyFormPanelProps) {
  const { t } = useLanguage();
  const [input, setInput] = useState<PartyInput>(
    initial ?? {
      name: '',
      type: PartyInputType.Individual,
      countryOfResidence: 'GB',
      netWorthCurrency: 'GBP',
    }
  );

  return (
    <div className='space-y-3'>
      <Input
        value={input.name}
        onChange={(event) => setInput({ ...input, name: event.target.value })}
        placeholder={t('settings.parties.name')}
      />
      <Select
        value={input.type}
        onValueChange={(type) => setInput({ ...input, type: type as PartyInput['type'] })}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PARTY_TYPES.map((type) => (
            <SelectItem key={type} value={type}>
              {type}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <CountrySelect
        value={input.countryOfResidence ?? ''}
        onChange={(countryOfResidence) => setInput({ ...input, countryOfResidence })}
        placeholder={t('settings.parties.country')}
      />
      <Input
        type='number'
        value={input.netWorth ?? ''}
        onChange={(event) => setInput({ ...input, netWorth: Number(event.target.value) })}
        placeholder={t('settings.parties.netWorth')}
      />
      <div className='flex justify-end gap-2 pt-2'>
        <Button type='button' variant='outline' onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type='button' onClick={() => void onSubmit(input)}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
